// v113-fixed
import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import FamilyGroup from './FamilyGroup';
import ShareCard from './ShareCard';
import { getRank, LEVEL_NAMES, DAILY_CHALLENGES, MILESTONES } from '../data/initialState';
import { t } from '../data/translations';
import { supabase } from '../supabase';
import { CAT_META, TIPS, XP_PER_LEVEL, PLAN_LIMITS } from '../data/initialState';
import Confetti from './Confetti';

const getCurrency = (lang) => lang==='en'?'$':lang==='da'?'kr (DKK)':lang==='sv'?'kr (SEK)':'kr';
const fmt = (n, lang) => {
  const locale = lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO';
  const num = Math.round(Math.abs(n)).toLocaleString(locale);
  if (lang==='en') return '$' + num;
  if (lang==='sv') return num + ' kr';
  if (lang==='da') return num + ' kr';
  return num + ' kr';
};

const getCheckinQuestions = (lang) => [
  { q:lang==='en'?'Avoided unnecessary purchases today?':lang==='sv'?'Undvikt onödiga köp idag?':lang==='da'?'Undgået unødvendige køb i dag?':'Har du unngått unødvendige kjøp i dag?', yes:'+20 XP ✅', no:lang==='en'?'Almost...':lang==='sv'?'Nästan...':lang==='da'?'Næsten...':'Nesten...' },
  { q:lang==='en'?'Brought lunch from home today?':lang==='sv'?'Tog du med lunchlåda idag?':lang==='da'?'Tog du madpakke med i dag?':'Tok du med matpakke i dag?', yes:'+15 XP ✅', no:lang==='en'?'Not today':lang==='sv'?'Inte idag':lang==='da'?'Ikke i dag':'Nei dessverre' },
  { q:lang==='en'?'Checked your balance today?':lang==='sv'?'Kollade du saldot idag?':lang==='da'?'Tjekkede du din saldo i dag?':'Sjekket du saldoen din i dag?', yes:'+10 XP ✅', no:lang==='en'?'Not yet':lang==='sv'?'Inte än':lang==='da'?'Ikke endnu':'Ikke ennå' },
];

const getPersonalizedTips = (lang, state) => {
  const allTips = getTipsFeed(lang, lang==='en'?'$':'kr');
  // Add personalized tips based on user data
  const personal = [];
  
  // If user has subscriptions, remind them
  const subs = (state.subscriptions || []);
  if (subs.length > 0) {
    const subTotal = subs.reduce((s, sub) => s + (sub.amount || 0), 0);
    personal.push({ emoji:'📦', 
      title: lang==='en'?'Your subscriptions':lang==='sv'?'Dina prenumerationer':lang==='da'?'Dine abonnementer':'Dine abonnementer',
      body: lang==='en'?`You spend ${lang==='en'?'$':''}${Math.round(subTotal)}${lang!=='en'?' kr':''}/month on ${subs.length} subscription${subs.length>1?'s':''}. Worth a review?`:
        lang==='sv'?`Du spenderar ${Math.round(subTotal)} kr/mån på ${subs.length} prenumerationer. Dags att se över?`:
        lang==='da'?`Du bruger ${Math.round(subTotal)} kr/mdr på ${subs.length} abonnementer. Værd at gennemgå?`:
        `Du bruker ${Math.round(subTotal)} kr/mnd på ${subs.length} abonnement${subs.length>1?'er':''}. Verdt å se over?`,
      tag: lang==='en'?'Personal':lang==='sv'?'Personlig':lang==='da'?'Personlig':'Personlig' });
  }
  
  // If user has goals, motivate them
  const goals = (state.goals || []).filter(g => g.target > 0);
  if (goals.length > 0) {
    const goal = goals[0];
    const pct = Math.round((goal.saved / goal.target) * 100);
    personal.push({ emoji:'🎯',
      title: lang==='en'?`Goal: ${goal.name}`:lang==='sv'?`Mål: ${goal.name}`:lang==='da'?`Mål: ${goal.name}`:`Mål: ${goal.name}`,
      body: lang==='en'?`You're ${pct}% there! Keep saving to reach your goal.`:
        lang==='sv'?`Du är ${pct}% där! Fortsätt spara för att nå ditt mål.`:
        lang==='da'?`Du er ${pct}% der! Bliv ved med at spare for at nå dit mål.`:
        `Du er ${pct}% der! Fortsett å spare for å nå målet ditt.`,
      tag: lang==='en'?'Goals':lang==='sv'?'Mål':lang==='da'?'Mål':'Mål' });
  }
  
  // If user spends on food, suggest matpakke tip
  const foodSpend = (state.transactions || [])
    .filter(t => t.cat === 'Mat' || t.cat === 'Food' || t.cat === 'Café & food')
    .reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  if (foodSpend > 0) {
    personal.push({ emoji:'🥗',
      title: lang==='en'?'Meal prep tip':lang==='sv'?'Matlådetips':lang==='da'?'Madpakketip':'Matpakke-tips',
      body: lang==='en'?`You spend on food — bring lunch 3 days a week and save up to ${lang==='en'?'$300':'300 kr'}/month!`:
        lang==='sv'?`Du spenderar på mat — ta med matlåda 3 dagar i veckan och spara upp till 300 kr/månaden!`:
        lang==='da'?`Du bruger på mad — tag madpakke 3 dage om ugen og spar op til 300 kr/måneden!`:
        `Du bruker penger på mat — ta med matpakke 3 dager i uka og spar opp til 300 kr/måneden!`,
      tag: lang==='en'?'Personal':lang==='sv'?'Personlig':lang==='da'?'Personlig':'Personlig' });
  }

  // If streak > 0, celebrate
  if ((state.streak || 0) > 2) {
    personal.push({ emoji:'🔥',
      title: lang==='en'?`${state.streak} day streak!`:lang==='sv'?`${state.streak} dagars streak!`:lang==='da'?`${state.streak} dages streak!`:`${state.streak} dagers streak!`,
      body: lang==='en'?'Amazing consistency! Keep it up!':lang==='sv'?'Fantastisk konsistens! Fortsätt!':lang==='da'?'Fantastisk konsistens! Bliv ved!':'Imponerende! Fortsett slik!',
      tag: lang==='en'?'Streak':lang==='sv'?'Streak':lang==='da'?'Streak':'Streak' });
  }
  
  // Mix personal + general, shuffle general so it's different each day
  const seed = new Date().getDate() + new Date().getMonth() * 31;
  const shuffled = [...allTips].sort((a, b) => {
    const ha = (seed * (allTips.indexOf(a)+1) * 2654435769) % 100;
    const hb = (seed * (allTips.indexOf(b)+1) * 2654435769) % 100;
    return ha - hb;
  });
  
  return [...personal, ...shuffled].slice(0, 8);
};

const getTipsFeed = (lang, currency) => {
  const c = currency || (lang==='en'?'$':'kr');
  const amt = (n) => lang==='en' ? `$${n}` : `${n} kr`;
  return [
  { emoji:'💡', title:lang==='en'?'Did you know?':lang==='sv'?'Visste du?':lang==='da'?'Vidste du?':'Visste du?',
    body:lang==='en'?`Cutting one coffee per day saves you ${amt(1400)} a year.`:lang==='sv'?`Att skippa ett kaffe om dagen sparar ${amt(1400)} på ett år.`:lang==='da'?`At springe en kaffe over om dagen sparer dig ${amt(1400)} om året.`:`Å kutte én kaffe per dag sparer deg ${amt(1400)} på et år.`,
    tag:lang==='en'?'Savings':lang==='sv'?'Sparande':lang==='da'?'Opsparing':'Sparing' },
  { emoji:'📊', title:lang==='en'?'Psychology tip':lang==='sv'?'Psykologitips':lang==='da'?'Psykologitip':'Psykologi-tips',
    body:lang==='en'?'Use cash for one week — people spend 20% less than with card.':lang==='sv'?'Använd kontanter en vecka — folk spenderar 20% mindre än med kort.':lang==='da'?'Brug kontanter en uge — folk bruger 20% mindre end med kort.':'Bruk kontanter en uke — folk bruker 20% mindre enn med kort.',
    tag:lang==='en'?'Behaviour':lang==='sv'?'Beteende':lang==='da'?'Adfærd':'Atferd' },
  { emoji:'🏦', title:lang==='en'?'BSU tip':lang==='sv'?'BSU-tips':lang==='da'?'BSU-tip':'BSU-tips',
    body:lang==='en'?`BSU gives you up to ${amt(5000)} in tax deductions per year — free money!`:lang==='sv'?`BSU ger dig upp till ${amt(5000)} i skatteavdrag per år — gratis pengar!`:lang==='da'?`BSU giver dig op til ${amt(5000)} i skattefradrag om året — gratis penge!`:`Har du BSU? Du får ${amt(5000)} i skattefradrag per år!`,
    tag:lang==='en'?'Investment':lang==='sv'?'Investering':lang==='da'?'Investering':'Investering' },
  { emoji:'⚡', title:lang==='en'?'Quick tip':lang==='sv'?'Snabbtips':lang==='da'?'Hurtigtip':'Hurtigtips',
    body:lang==='en'?'Set up automatic transfer to savings account right after payday.':lang==='sv'?'Sätt upp automatisk överföring till sparkonto direkt efter lönedagen.':lang==='da'?'Opsæt automatisk overførsel til opsparingskonto lige efter lønningsdag.':'Sett opp automatisk overføring til sparekonto rett etter lønning.',
    tag:lang==='en'?'Automation':lang==='sv'?'Automatisering':lang==='da'?'Automatisering':'Automatisering' },
  { emoji:'🎯', title:lang==='en'?'50/30/20 rule':lang==='sv'?'50/30/20-regeln':lang==='da'?'50/30/20-reglen':'50/30/20-regelen',
    body:lang==='en'?'50% needs, 30% wants, 20% savings.':lang==='sv'?'50% behov, 30% önskemål, 20% sparande.':lang==='da'?'50% behov, 30% ønsker, 20% opsparing.':'50% til nødvendigheter, 30% til ønsker, 20% til sparing.',
    tag:lang==='en'?'Budget':lang==='sv'?'Budget':lang==='da'?'Budget':'Budsjett' },
  { emoji:'🥗', title:lang==='en'?'Meal prep tip':lang==='sv'?'Matlådetips':lang==='da'?'Madpakketip':'Matpakke-tips',
    body:lang==='en'?`Bring lunch 3 days a week and save up to ${amt(300)} monthly.`:lang==='sv'?`Ta med matlåda 3 dagar i veckan och spara upp till ${amt(300)} i månaden.`:lang==='da'?`Tag madpakke 3 dage om ugen og spar op til ${amt(300)} om måneden.`:`Ta med matpakke 3 dager i uka og spar opp til ${amt(300)} per måned.`,
    tag:lang==='en'?'Food':lang==='sv'?'Mat':lang==='da'?'Mad':'Mat' },
  { emoji:'📱', title:lang==='en'?'Subscription check':lang==='sv'?'Prenumerationskoll':lang==='da'?'Abonnementstjek':'Abonnement-sjekk',
    body:lang==='en'?'The average person pays for 3 subscriptions they forgot about.':lang==='sv'?'En genomsnittlig person betalar för 3 prenumerationer de glömt bort.':lang==='da'?'Den gennemsnitlige person betaler for 3 abonnementer de glemte.':lang==='sv'?'Genomsnittspersonen betalar för 3 prenumerationer de glömt.':lang==='da'?'Gennemsnitspersonen betaler for 3 abonnementer de glemte.':'Gjennomsnittspersonen betaler for 3 abonnementer de har glemt.',
    tag:lang==='en'?'Savings':lang==='sv'?'Sparande':lang==='da'?'Opsparing':'Sparing' },
];
};
export default function TabHjem({ onNav, syncTransaction, syncGoal }) {
  const { state, dispatch } = useApp();
  const lang = state.language || 'en';
  const hasGoal = (state.goals || []).length > 0;
  const isPro = state.plan === 'advanced' || state.plan === 'premium';
  
  // Savvy welcome for new users
  

  const [firstSaveWow, setFirstSaveWow] = React.useState(false);
  const totalSaved = (state.transactions || []).filter(t => t.cat === 'Sparing').reduce((s,t) => s+Math.abs(t.amount), 0);
  const prevTotalRef = React.useRef(totalSaved);

  React.useEffect(() => {
    if (totalSaved > 0 && prevTotalRef.current === 0) {
      setFirstSaveWow(true);
      setTimeout(() => setFirstSaveWow(false), 3000);
    }
    prevTotalRef.current = totalSaved;
  }, [totalSaved]);
  const [show7DayChallenge, setShow7DayChallenge] = React.useState(false);
  const [showShareCard, setShowShareCard] = React.useState(false);
  const [commitmentDone, setCommitmentDone] = React.useState(
    localStorage.getItem('savely_commitment') === 'true'
  );

  // Viral share streak
  const shareStreak = () => {
    const text = lang === 'en'
      ? `🔥 I'm on a ${state.streak}-day saving streak with Savely! Can you beat me? mysavely.app`
      : lang === 'sv'
      ? `🔥 Jag har ${state.streak} dagars sparstreak med Savely! Kan du slå mig? mysavely.app`
      : lang === 'da'
      ? `🔥 Jeg har ${state.streak} dages spare-streak med Savely! Kan du slå mig? mysavely.app`
      : `🔥 Jeg har ${state.streak} dagers streak på Savely! Klarer du å slå meg? mysavely.app`;
    if (navigator.share) {
      navigator.share({ title: 'Savely', text, url: 'https://mysavely.app' });
    } else {
      navigator.clipboard.writeText(text);
      alert(lang === 'en' ? '✅ Copied! Share on TikTok or Instagram 🚀' : lang==='sv'?'✅ Kopierat! Dela på TikTok eller Instagram 🚀':lang==='da'?'✅ Kopieret! Del på TikTok eller Instagram 🚀':'✅ Kopiert! Del på TikTok eller Instagram 🚀');
    }
  };

  // Commitment mechanic
  const makeCommitment = () => {
    localStorage.setItem('savely_commitment', 'true');
    setCommitmentDone(true);
    dispatch({ type: 'ADD_XP', payload: 50 });
  };

  // Daily challenge setup
  React.useEffect(() => {
    if (!state.user) return;
    const today = new Date().toDateString();
    if (!state.dailyChallenge || state.dailyChallengeDate !== today) {
      // Use date as seed for daily variety
      const today2 = new Date();
      const seed = today2.getFullYear() * 10000 + (today2.getMonth()+1) * 100 + today2.getDate();
      const challengeIdx = seed % DAILY_CHALLENGES.length;
      dispatch({ type: 'SET_DAILY_CHALLENGE', payload: DAILY_CHALLENGES[challengeIdx] });
    }
  }, [state.user]);

  // Check milestone
  React.useEffect(() => {
    if (!state.balance) return;
    const reached = MILESTONES.filter(m => state.balance >= m);
    if (reached.length > 0) {
      const latest = reached[reached.length - 1];
      const key = `milestone_${latest}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, '1');
        dispatch({ type: 'SET_MILESTONE', payload: latest });
        setTimeout(() => dispatch({ type: 'CLEAR_MILESTONE' }), 5000);
      }
    }
  }, [state.balance]);

  // Fetch friends saving today
  React.useEffect(() => {
    if (!state.user?.id) return;
    const fetchFriends = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const { data: friendships } = await supabase
          .from('friendships')
          .select('requester_id, receiver_id')
          .or('requester_id.eq.' + state.user.id + ',receiver_id.eq.' + state.user.id)
          .eq('status', 'accepted');
        if (!friendships?.length) return;
        const friendIds = friendships.map(f => f.requester_id === state.user.id ? f.receiver_id : f.requester_id);
        const { data: txs } = await supabase
          .from('transactions')
          .select('user_id')
          .in('user_id', friendIds)
          .gte('date', today);
        const unique = new Set(txs?.map(t => t.user_id) || []);
        dispatch({ type: 'SET_FRIENDS_SAVING', payload: unique.size });
      } catch (e) {}
    };
    fetchFriends();
  }, [state.user?.id]);

  // Rank
  const rank = getRank(state.xp || 0);
  const levelNum = Math.max(1, Math.min(Math.floor((state.xp || 0) / 500) + 1, 10));
  const levelNames = {
    en: ['',lang==='en'?'Beginner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner','Saver','On track','Save Friend','Save Pro','Budget Guru','Master Saver','Money Wisdom','Economy Ninja','Finance Master'],
    no: ['',(lang==='en'?lang==='en'?'Beginner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner'),'Spareforsøk','På vei','Sparevenn','Spareproff','Budsjett-guru','Mestersparer','Pengevisdom','Økonomi-ninja','Finansmester'],
    sv: ['','Nybörjare','Sparare','På rätt spår','Sparvän','Sparse Pro','Budget Guru','Mästarsparare','Pengavisdom','Ekonomi Ninja','Finansmästare'],
    da: ['','Begynder','Opsparer','På vej','Spareven','Spare Pro','Budget Guru','Mestersparer','Pengevisdom','Økonomi Ninja','Finansmester'],
  };
  const rankName = (levelNames[lang] || levelNames.en)[levelNum] || lang==='en'?'Beginner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner';

  // Check daily challenge completion
  const checkChallengeComplete = (saveAmt) => {
    if (!state.dailyChallenge || state.dailyChallengeCompleted) return;
    const ch = state.dailyChallenge;
    const today = new Date().toDateString();
    const todaySaved = (state.transactions || []).filter(t => t.cat === 'Sparing' && new Date(t.date).toDateString() === today).reduce((s,t) => s+Math.abs(t.amount), 0);
    if (ch.type === 'save' && (todaySaved + saveAmt) >= ch.target) {
      dispatch({ type: 'COMPLETE_DAILY_CHALLENGE', payload: ch.xp });
    }
    if (ch.type === 'streak' && saveAmt > 0) {
      dispatch({ type: 'COMPLETE_DAILY_CHALLENGE', payload: ch.xp });
    }
  };

  // Mascot messages
  const getMascotMsg = () => {
    const todaySaved = (state.transactions || []).filter(t => t.cat==='Sparing' && new Date(t.date).toDateString()===new Date().toDateString()).reduce((s,t)=>s+Math.abs(t.amount),0);
    const todaySpent = (state.transactions || []).filter(t => t.amount<0 && t.cat!=='Sparing' && new Date(t.date).toDateString()===new Date().toDateString()).reduce((s,t)=>s+Math.abs(t.amount),0);
    const junkSpent = (state.transactions || []).filter(t => ['Mat','Underholdning','Shopping','Food','Entertainment'].includes(t.cat) && new Date(t.date).toDateString()===new Date().toDateString()).reduce((s,t)=>s+Math.abs(t.amount),0);
    const hour = new Date().getHours();
    const name = state.username || state.user?.username || state.user?.user_metadata?.full_name?.split(' ')[0] || state.user?.email?.split('@')[0] || '';
    const curr = lang==='en'?'$':lang==='da'?'DKK':lang==='sv'?'SEK':'kr';
    const fmtAmt = (n) => lang==='en'?`$${Math.round(n)}`:`${Math.round(n)} ${curr}`;
    const dayOfWeek = new Date().getDay();
    const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const monthSaved = (state.transactions || []).filter(t=>t.cat==='Sparing'&&new Date(t.date)>=startOfMonth).reduce((s,t)=>s+Math.abs(t.amount),0);
    
    // Fredag-advarsel
    if (dayOfWeek === 5 && hour >= 15) {
      return {en:`It's Friday ${name}... Savvy is watching your wallet 👀`,no:`Det er fredag ${name}... Savvy følger med på lommeboken din 👀`,sv:`Det är fredag ${name}... Savvy håller koll på plånboken 👀`,da:`Det er fredag ${name}... Savvy holder øje med lommebogen 👀`}[lang]||`Det er fredag ${name}... Savvy følger med 👀`;
    }
    // Mandag-motivasjon
    if (dayOfWeek === 1 && hour < 12) {
      return {en:`New week, new chance to crush it ${name}! 💪`,no:`Ny uke, ny sjanse til å knuse det ${name}! 💪`,sv:`Ny vecka, ny chans att rocka det ${name}! 💪`,da:`Ny uge, ny chance til at klare det ${name}! 💪`}[lang]||`Ny uke, ny sjanse ${name}! 💪`;
    }
    // Sent på kvelden
    if (hour >= 22) {
      return {en:`Sleepy time... but did you save today? 😴`,no:`Leggetid... men sparte du noe i dag? 😴`,sv:`Dags att sova... men sparade du idag? 😴`,da:`Sengetid... men sparede du noget i dag? 😴`}[lang]||`Leggetid... men sparte du noe? 😴`;
    }
    // God morgen
    if (hour < 9) {
      return {en:`Good morning ${name}! Fresh day, fresh budget 🌅`,no:`God morgen ${name}! Ny dag, nytt budsjett 🌅`,sv:`God morgon ${name}! Ny dag, ny budget 🌅`,da:`God morgen ${name}! Ny dag, nyt budget 🌅`}[lang]||`God morgen ${name}! 🌅`;
    }
    // Ingen streak - første dag
    if (state.streak === 0 && state.balance === 0) {
      return {en:`Hey ${name}! Save your first ${curr} today and I'll do a happy dance 🌱`,no:`Hei ${name}! Spar din første krone i dag, og jeg danser litt 🌱`,sv:`Hej ${name}! Spara din första krona idag, så dansar jag lite 🌱`,da:`Hej ${name}! Spar din første krone i dag, så danser jeg lidt 🌱`}[lang]||`Hei ${name}! 🌱`;
    }
    // Lang streak
    if (state.streak >= 30) { setMascotMood('flex');
      return {en:`${state.streak} days! You're basically a saving LEGEND ${name} 👑`,no:`${state.streak} dager! Du er nesten en spare-LEGENDE ${name} 👑`,sv:`${state.streak} dagar! Du är nästan en spare-LEGEND ${name} 👑`,da:`${state.streak} dage! Du er næsten en spare-LEGENDE ${name} 👑`}[lang]||`${state.streak} dager! Legende! 👑`;
    }
    if (state.streak >= 7) {
      return {en:`${state.streak} day streak! You're on FIRE ${name} 🔥`,no:`${state.streak} dagers streak! Du er i FYR OG FLAMME ${name} 🔥`,sv:`${state.streak} dagars streak! Du är i FART ${name} 🔥`,da:`${state.streak} dages streak! Du er VARM ${name} 🔥`}[lang]||`${state.streak} dagers streak! 🔥`;
    }
    // Spart i dag
    if (todaySaved > 0) {
      return {en:`You saved ${fmtAmt(todaySaved)} today! Savvy is proud 🥹`,no:`Du sparte ${fmtAmt(todaySaved)} i dag! Savvy er stolt 🥹`,sv:`Du sparade ${fmtAmt(todaySaved)} idag! Savvy är stolt 🥹`,da:`Du sparede ${fmtAmt(todaySaved)} i dag! Savvy er stolt 🥹`}[lang]||`Du sparte ${fmtAmt(todaySaved)} i dag! 🥹`;
    }
    // Holdt budsjettet
    if (todaySpent < state.dailyBudget * 0.5 && todaySpent > 0) {
      return {en:`You kept the budget today ${name} 😎 Savvy approves!`,no:`Du holdt budsjettet i dag ${name} 😎 Savvy godkjenner!`,sv:`Du höll budgeten idag ${name} 😎 Savvy godkänner!`,da:`Du holdt budgettet i dag ${name} 😎 Savvy godkender!`}[lang]||`Du holdt budsjettet i dag 😎`;
    }
    // Høyt forbruk
    if (junkSpent > state.dailyBudget * 0.7 && junkSpent > 0) {
      return {en:`You spent ${fmtAmt(junkSpent)} today... not mad, just disappointed 😤`,no:`Du brukte ${fmtAmt(junkSpent)} i dag... ikke sint, bare skuffet 😤`,sv:`Du spenderade ${fmtAmt(junkSpent)} idag... inte arg, bara besviken 😤`,da:`Du brugte ${fmtAmt(junkSpent)} i dag... ikke vred, bare skuffet 😤`}[lang]||`Du brukte ${fmtAmt(junkSpent)}... ikke sint, bare skuffet 😤`;
    }
    // God måned
    if (monthSaved > state.income * 0.1 && monthSaved > 0) {
      return {en:`You've saved ${fmtAmt(monthSaved)} this month! That's real money ${name} 💰`,no:`Du har spart ${fmtAmt(monthSaved)} denne måneden! Det er ekte penger ${name} 💰`,sv:`Du har sparat ${fmtAmt(monthSaved)} denna månad! Det är riktiga pengar ${name} 💰`,da:`Du har sparet ${fmtAmt(monthSaved)} denne måned! Det er rigtige penge ${name} 💰`}[lang]||`Du har spart ${fmtAmt(monthSaved)} denne måneden! 💰`;
    }
    // Default morsomme meldinger
    const defaults = {
      en:[`Most people spend more on coffee than savings. Not you though... right? ☕`,`Savvy believes in you ${name}! Even when the budget doesn't 😅`,`One saved ${curr} is better than zero saved ${curr}. That's math. 📊`,`Your future self will thank you for this 🙏`],
      no:[`De fleste bruker mer på kaffe enn sparing. Ikke du... eller? ☕`,`Savvy tror på deg ${name}! Selv når budsjettet ikke gjør det 😅`,`En spart krone er bedre enn null sparte kroner. Det er matte. 📊`,`Fremtidig deg vil takke deg for dette 🙏`],
      sv:[`De flesta spenderar mer på kaffe än sparande. Inte du... eller? ☕`,`Savvy tror på dig ${name}! Även när budgeten inte gör det 😅`,`En sparad krona är bättre än noll sparade kronor. Det är matte. 📊`,`Ditt framtida jag kommer tacka dig för detta 🙏`],
      da:[`De fleste bruger mere på kaffe end opsparing. Ikke dig... vel? ☕`,`Savvy tror på dig ${name}! Selv når budgettet ikke gør det 😅`,`En sparet krone er bedre end nul sparede kroner. Det er matematik. 📊`,`Dit fremtidige jeg vil takke dig for dette 🙏`],
    };
    const arr = defaults[lang] || defaults.no;
    return arr[Math.floor(Date.now()/3600000) % arr.length];
  };

  // Streak chain last 7 days
  const getStreakChain = () => {
    return Array.from({length: 7}, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (6 - i));
      const dateStr = d.toDateString();
      const hasSaved = (state.transactions || []).some(t => t.cat === 'Sparing' && new Date(t.date).toDateString() === dateStr);
      return { date: d, hasSaved, isToday: i === 6 };
    });
  };
  const streakChain = getStreakChain();
  const [tipIdx] = useState(Math.floor(Math.random() * TIPS.length));
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinStep, setCheckinStep] = useState(0);
  const [checkinDone, setCheckinDone] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [savvyCelebrate, setSavvyCelebrate] = useState(false);
  const [showGraph, setShowGraph] = useState(true);
  const [graphPeriod, setGraphPeriod] = useState('week');
  const [graphOffset, setGraphOffset] = useState(0); // 0 = current, -1 = last week, etc
  const [saveAmount, setSaveAmount] = useState('');
  const [saveGoalId, setSaveGoalId] = useState(null);
  const [saveDone, setSaveDone] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [showMascot, setShowMascot] = useState(false);
  
  // First visit tab tips
  React.useEffect(() => {
    const visited = localStorage.getItem('savely_visited_hjem');
    if (!visited && state.user) {
      localStorage.setItem('savely_visited_hjem', '1');
      setTimeout(() => {
        const tips = {
          en: `Welcome ${state.user?.username||state.user?.name||''}! 👋 Add your first expense to get started 💰`,
          no: `Velkommen ${state.user?.username||state.user?.name||''}! 👋 ${lang==='en'?'Add your first expense to get started 💰':lang==='sv'?'Lägg till din första utgift för att komma igång 💰':lang==='da'?'Tilføj din første udgift for at komme i gang 💰':'Legg til din første utgift for å komme i gang 💰'}`,
          sv: `Välkommen ${state.user?.username||state.user?.name||''}! 👋 Lägg till din första utgift för att komma igång 💰`,
          da: `Velkommen ${state.user?.username||state.user?.name||''}! 👋 Tilføj din første udgift for at komme i gang 💰`,
        };
        setMascotMsg(tips[lang] || tips.en);
        setMascotMood('happy');
        setShowMascot(true);
        setTimeout(() => setShowMascot(false), 6000);
      }, 2000);
    }
  }, [state.user]);
  const [mascotMsg, setMascotMsg] = useState('');
  const [mascotMood, setMascotMood] = useState('happy');

  // Mascot trigger
  React.useEffect(() => {
    const isNew = !localStorage.getItem('savely_welcomed');
    if (isNew && state.user) {
      const name = state.username || state.user?.username || state.user?.user_metadata?.full_name?.split(' ')[0] || state.user?.email?.split('@')[0] || '';
      const msgs = {
        en: `Hi ${name}! 👋 I'm Savvy, your saving buddy! Let's crush those goals! 🌱`,
        no: `Hei ${name}! 👋 Jeg er Savvy, sparekompisen din! La oss nå målene dine! 🌱`,
        sv: `Hej ${name}! 👋 Jag är Savvy, din sparekompis! Låt oss nå dina mål! 🌱`,
        da: `Hej ${name}! 👋 Jeg er Savvy, din sparevan! Lad os nå dine mål! 🌱`,
      };
      setMascotMsg(msgs[lang] || msgs.en);
      setMascotMood('happy');
      setShowMascot(true);
      localStorage.setItem('savely_welcomed', '1');
      setTimeout(() => setShowMascot(false), 5000);
    }
  }, [state.user]);

  // Mascot on milestone
  React.useEffect(() => {
    if (state.milestoneReached) {
      const msgs = {
        en: `🎉 WOW! You've saved ${state.milestoneReached} kr! You're amazing!`,
        no: `🎉 WOW! Du har spart ${state.milestoneReached} kr! Du er fantastisk!`,
        sv: `🎉 WOW! Du har sparat ${state.milestoneReached} kr! Du är grym!`,
        da: `🎉 WOW! Du har sparet ${state.milestoneReached} kr! Du er fantastisk!`,
      };
      setMascotMsg(msgs[lang] || msgs.en);
      setMascotMood('star');
      setShowMascot(true);
      setTimeout(() => setShowMascot(false), 5000);
    }
  }, [state.milestoneReached]);

  // Mascot on streak milestone
  React.useEffect(() => {
    if (state.streak > 0 && state.streak % 7 === 0) {
      const msgs = {
        en: `🔥 ${state.streak} day streak! You're on fire! Keep it up!`,
        no: `🔥 ${state.streak} dagers streak! Du er på fyret! Fortsett!`,
        sv: `🔥 ${state.streak} dagars streak! Du är fantastisk! Fortsätt!`,
        da: `🔥 ${state.streak} dages streak! Du er fantastisk! Fortsæt!`,
      };
      setMascotMsg(msgs[lang] || msgs.en);
      setMascotMood('party');
      setShowMascot(true);
      setTimeout(() => setShowMascot(false), 5000);
    }
  }, [state.streak]);

  // Maskot reagerer på høyt forbruk
  React.useEffect(() => {
    const hour = new Date().getHours();
    const junkSpent = state.transactions
      .filter(t => ['Mat','Underholdning','Shopping'].includes(t.cat) && new Date(t.date).toDateString() === new Date().toDateString())
      .reduce((s,t) => s + Math.abs(t.amount), 0);
    
    if (junkSpent > 300 && !showMascot) {
      const msgs = {
        en: `You spent ${Math.round(junkSpent)} kr today... I'm not mad, just disappointed 😤`,
        no: `Du brukte ${Math.round(junkSpent)} kr i dag... jeg er ikke sint, bare skuffet 😤`,
        sv: `Du spenderade ${Math.round(junkSpent)} kr idag... jag är inte arg, bara besviken 😤`,
        da: `Du brugte ${Math.round(junkSpent)} kr i dag... jeg er ikke sur, bare skuffet 😤`,
      };
      setMascotMsg(msgs[lang] || msgs.en);
      setMascotMood('angry');
      setShowMascot(true);
      setTimeout(() => setShowMascot(false), 5000);
    }
    if (hour >= 22 && !showMascot && state.streak > 0) {
      const msgs = {
        en: `Zzzz... don't forget to save before bed! 💤`,
        no: `Zzzz... ikke glem å spare før du legger deg! 💤`,
        sv: `Zzzz... glöm inte att spara innan du sover! 💤`,
        da: `Zzzz... glem ikke at spare inden du sover! 💤`,
      };
      setMascotMsg(msgs[lang] || msgs.en);
      setMascotMood('sleepy');
      setShowMascot(true);
      setTimeout(() => setShowMascot(false), 5000);
    }
  }, [state.transactions]);

  // Sjekk push-status
  React.useEffect(() => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  const enablePush = async () => {
    if (!('Notification' in window)) {
      alert(lang==='en'?'Add Savely to your home screen first, then enable push notifications! (Share → Add to Home Screen)':lang==='sv'?'Installera appen på din hemskärm för att aktivera pushnotiser!':lang==='da'?'Installer appen på din startskærm for at aktivere push-notifikationer!':lang==='en'?'Install the app on your home screen to enable push notifications!':lang==='sv'?'Installera appen på din startskärm för att aktivera push-notiser!':lang==='da'?'Installer appen på din startskærm for at aktivere push-beskeder!':'Installer appen på hjemskjermen for å aktivere push-varsler!');
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setPushEnabled(true);
      // Show immediate confirmation
      new Notification('🌱 Savely', {
        body: lang==='en'?"You'll get daily reminders at 6 PM! 💰":lang==='sv'?'Du får dagliga påminnelser kl 18! 💰':lang==='da'?'Du får daglige påmindelser kl. 18! 💰':'Du får daglige påminnelser kl 18! 💰',
        icon: '/icon.svg'
      });
      // Schedule daily reminder via service worker
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        reg.active?.postMessage({
          type: 'SCHEDULE_DAILY_REMINDER',
          message: lang==='en'?"Time to check your budget! 💰 Don't break your streak!":lang==='sv'?'Dags att kolla din budget! 💰 Bryt inte din streak!':lang==='da'?'Tid til at tjekke dit budget! 💰 Bryd ikke din streak!':'Sjekk budsjettet ditt! 💰 Ikke bryt streaken!',
        });
      }
    }
  };

  const shareGoal = (goal) => {
    const text = `🎯 Jeg sparer til ${goal.name} med Savely! ${(goal.target > 0 ? Math.round(goal.saved/goal.target*100) : 0)}% ferdig (${goal.saved.toLocaleString(lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO')} av ${goal.target.toLocaleString(lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO')} kr) 💰 Bli med på mysavely.app`;
    if (navigator.share) {
      navigator.share({ title: lang==='en'?'Savely savings goal':lang==='sv'?'Savely sparmål':lang==='da'?'Savely sparemål':'Savely sparemål', text, url: 'https://mysavely.app' });
    } else {
      navigator.clipboard.writeText(text);
      alert(lang==='en'?'✅ Copied! Share on TikTok or Instagram 🚀':lang==='sv'?'✅ Kopierat! Dela på TikTok eller Instagram 🚀':lang==='da'?'✅ Kopieret! Del på TikTok eller Instagram 🚀':'✅ Kopiert! Del på TikTok eller Instagram 🚀');
    }
  };

  // Beregn spart per uke/måned/år
  const now = new Date();
  const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const savedThisWeek = (state.transactions || []).filter(t => t.cat === 'Sparing' && new Date(t.date) >= startOfWeek).reduce((s,t) => s + Math.abs(t.amount), 0);
  const savedThisMonth = (state.transactions || []).filter(t => t.cat === 'Sparing' && new Date(t.date) >= startOfMonth).reduce((s,t) => s + Math.abs(t.amount), 0);
  const savedThisYear = (state.transactions || []).filter(t => t.cat === 'Sparing' && new Date(t.date) >= startOfYear).reduce((s,t) => s + Math.abs(t.amount), 0);
  
  // Forrige måned sammenligning
  const prevMonthStart = new Date(new Date().getFullYear(), new Date().getMonth()-1, 1);
  const prevMonthEnd = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const spentThisMonth = (state.transactions || []).filter(t => t.amount<0 && t.cat!=='Sparing' && new Date(t.date)>=startOfMonth).reduce((s,t)=>s+Math.abs(t.amount),0);
  const spentPrevMonth = (state.transactions || []).filter(t => t.amount<0 && t.cat!=='Sparing' && new Date(t.date)>=prevMonthStart && new Date(t.date)<prevMonthEnd).reduce((s,t)=>s+Math.abs(t.amount),0);
  const spentDiff = spentPrevMonth > 0 ? Math.round((spentThisMonth - spentPrevMonth) / spentPrevMonth * 100) : 0;
  const savedPrevMonth = (state.transactions || []).filter(t => t.cat==='Sparing' && new Date(t.date)>=prevMonthStart && new Date(t.date)<prevMonthEnd).reduce((s,t)=>s+Math.abs(t.amount),0);
  const savedDiff = savedPrevMonth > 0 ? Math.round((savedThisMonth - savedPrevMonth) / savedPrevMonth * 100) : 0;

  // Motivasjonsmelding
  const getMotivation = () => {
    const fmtNum = (n) => Math.round(n).toLocaleString(lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO');
    const curr = lang==='en'?'$':lang==='da'?'DKK':lang==='sv'?'SEK':'kr';
    const showAmt = (n) => lang==='en'?`$${fmtNum(n)}`:`${fmtNum(n)} ${curr}`;
    if (state.streak >= 7) return lang==='en'?`🔥 ${state.streak} day streak! You're unstoppable!`:lang==='sv'?`🔥 ${state.streak} dagars streak! Du är ostopp bar!`:lang==='da'?`🔥 ${state.streak} dages streak! Du er ustoppelig!`:`🔥 ${state.streak} dagers streak! Du er ustoppelig!`;
    if (savedThisWeek > 0) return lang==='en'?`⭐ You saved ${showAmt(savedThisWeek)} this week. Great job!`:lang==='sv'?`⭐ Du sparade ${showAmt(savedThisWeek)} denna vecka. Bra jobbat!`:lang==='da'?`⭐ Du sparede ${showAmt(savedThisWeek)} denne uge. Godt arbejde!`:`⭐ Du sparte ${showAmt(savedThisWeek)} denne uken. Bra jobba!`;
    if (savedThisMonth > 0) return lang==='en'?`💪 ${showAmt(savedThisMonth)} saved this month. Keep it up!`:lang==='sv'?`💪 ${showAmt(savedThisMonth)} sparat denna månad. Fortsätt så!`:lang==='da'?`💪 ${showAmt(savedThisMonth)} sparet denne måned. Fortsæt sådan!`:`💪 ${showAmt(savedThisMonth)} spart denne måneden. Fortsett slik!`;
    if (state.balance > 0) return lang==='en'?`🌱 Total ${showAmt(state.balance)} saved. You're on the right track!`:lang==='sv'?`🌱 Totalt ${showAmt(state.balance)} sparat. Du är på rätt väg!`:lang==='da'?`🌱 I alt ${showAmt(state.balance)} sparet. Du er på rette vej!`:`🌱 Totalt ${showAmt(state.balance)} spart. Du er på rett vei!`;
    return t(lang,'budgetUpdates');
  };

  const limits = PLAN_LIMITS[state.plan] || PLAN_LIMITS.free;
  const remaining = state.dailyBudget - state.dailySpent;
  const pct = state.dailyBudget > 0 ? Math.max(0, remaining / state.dailyBudget) : 1;
  const budgetColor = pct > 0.5 ? '#22C97A' : pct > 0.2 ? '#F5C518' : '#FF4D4D';
  const budgetBg = pct > 0.5 ? 'var(--g50)' : pct > 0.2 ? '#FFF9E6' : 'var(--r50)';
  const budgetLabel = pct > 0.5 ? (lang==='en'?'🟢 Great control':lang==='sv'?'🟢 Bra kontroll':lang==='da'?'🟢 God kontrol':'🟢 God kontroll') : pct > 0.2 ? (lang==='en'?'🟡 Watch out':lang==='sv'?'🟡 Se upp':lang==='da'?'🟡 Pas på':'🟡 Vær forsiktig') : (lang==='en'?'🔴 Overspending':lang==='sv'?'🔴 Överskrider':lang==='da'?'🔴 Overforbrug':'🔴 Over budsjett');
  const xpInLevel = state.xp % XP_PER_LEVEL;
  const xpPct = Math.round(xpInLevel / XP_PER_LEVEL * 100);
  const hasSetup = state.dailyBudget > 0;
  const _now = new Date();
  const daysLeft = (() => {
    if (state.payDay && state.payDay >= 1 && state.payDay <= 31) {
      const today = _now.getDate();
      if (today <= state.payDay) return Math.max(1, state.payDay - today);
      const daysInMonth = new Date(_now.getFullYear(), _now.getMonth()+1, 0).getDate();
      return (daysInMonth - today) + state.payDay;
    }
    return 30; // No payDay = full month
  })();
  const myScore = state.streak || 0; // Score = streak dager (samme som leaderboard)
  const saveAmt = parseFloat(saveAmount) || 0;
  const selectedGoal = (state.goals || []).find(g => g.id === (saveGoalId || state.goals[0]?.id));
  const afterSave = remaining - saveAmt;
  const overBudget = saveAmt > remaining;
  const goalRemaining = selectedGoal ? selectedGoal.target - selectedGoal.saved : 0;
  const overGoal = saveAmt > goalRemaining && !!selectedGoal;
  const lowWarning = afterSave <= 200 && afterSave >= 0 && !overBudget && saveAmt > 0;

  const handleSave = () => {
    checkChallengeComplete(saveAmt);
    if (!saveAmt || saveAmt <= 0 || overBudget || overGoal) return;
    const goalId = saveGoalId || state.goals[0]?.id;
    if (!goalId) return;
    const goal = (state.goals || []).find(g => g.id === goalId);
    const willComplete = goal && (goal.saved + saveAmt) >= goal.target;
    dispatch({ type:'SAVE_TO_GOAL', payload:{ goalId, amount: saveAmt } });
    const newTx = {
      id: Date.now(),
      desc: `Sparing — ${goal?.name || lang==='en'?'goal':lang==='sv'?'mål':lang==='da'?'mål':'mål'}`,
      cat: 'Sparing',
      amount: -saveAmt,
      date: 'I dag',
      time: new Date().toTimeString().slice(0,5),
      goalCompleted: willComplete ? true : undefined,
    };
    dispatch({ type:'ADD_TRANSACTION', payload: newTx });
    if (syncTransaction) syncTransaction(newTx, 'add');
    if (syncGoal) syncGoal({ ...goal, saved: Math.min(goal.target, goal.saved + saveAmt) }, 'update');
    if (willComplete) setConfetti(true);
    setSaveDone(true);
    setSaveAmount('');
    setTimeout(() => setSaveDone(false), 2500);
  };

  const handleCheckin = (yes) => {
    if (yes) dispatch({ type:'ADD_XP', payload: checkinStep === 0 ? 20 : 15 });
    if (checkinStep < getCheckinQuestions(lang).length - 1) setCheckinStep(checkinStep + 1);
    else { setCheckinDone(true); setShowCheckin(false); }
  };


  // Graph computation with offset
  const _graphNow = new Date();
  // Apply offset based on period
  const _graphRef = new Date(_graphNow);
  if (graphPeriod === 'week') _graphRef.setDate(_graphNow.getDate() + (graphOffset * 7));
  else if (graphPeriod === 'month') _graphRef.setMonth(_graphNow.getMonth() + graphOffset);
  else _graphRef.setFullYear(_graphNow.getFullYear() + graphOffset);

  let _graphStart;
  if (graphPeriod === 'week') { _graphStart = new Date(_graphRef); _graphStart.setDate(_graphRef.getDate()-6); }
  else if (graphPeriod === 'month') { _graphStart = new Date(_graphRef.getFullYear(), _graphRef.getMonth(), 1); }
  else { _graphStart = new Date(_graphRef.getFullYear(), 0, 1); }
  const _graphDays = Math.ceil((_graphNow-_graphStart)/(1000*60*60*24)) + 1;
  const _graphLabels = Array.from({length: Math.min(_graphDays,7)}, (_,i) => {
    const d = new Date(_graphRef); d.setDate(_graphRef.getDate() - (Math.min(_graphDays,7)-1-i));
    return d;
  });
  const _graphDaySaved = (d) => (state.transactions || []).filter(t => t.cat==='Sparing' && new Date(t.date).toDateString()===d.toDateString()).reduce((s,t)=>s+Math.abs(t.amount),0);
  const _graphDaySpent = (d) => (state.transactions || []).filter(t => t.amount < 0 && t.cat!=='Sparing' && new Date(t.date).toDateString()===d.toDateString()).reduce((s,t)=>s+Math.abs(t.amount),0);
  const _graphMaxSaved = Math.max(1, ..._graphLabels.map(d => Math.max(_graphDaySaved(d), _graphDaySpent(d))));


  // Rekorder
  const allWeeks = [];
  for(let i=0; i<12; i++) {
    const weekStart = new Date(); weekStart.setDate(weekStart.getDate() - (i*7));
    const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() - ((i-1)*7));
    const wSaved = (state.transactions || []).filter(t=>t.cat==='Sparing'&&new Date(t.date)>=weekStart&&new Date(t.date)<weekEnd).reduce((s,t)=>s+Math.abs(t.amount),0);
    allWeeks.push(wSaved);
  }
  const bestWeek = Math.max(0, ...allWeeks);
  const isNewRecord = savedThisWeek > 0 && savedThisWeek >= bestWeek;


  return (
    <div style={{ padding:'1.25rem 1rem 1rem' }}>
      <Confetti active={confetti} onDone={() => setConfetti(false)}/>

      {/* Milestone popup */}
      {state.milestoneReached && (
        <div style={{ position:'fixed', top:80, left:'50%', transform:'translateX(-50%)', background:'linear-gradient(135deg,#FFD700,#FFA500)', color:'#000', borderRadius:'var(--r-full)', padding:'12px 24px', fontSize:15, fontWeight:800, zIndex:1000, boxShadow:'0 4px 20px rgba(255,165,0,0.5)', whiteSpace:'nowrap' }}>
          🎉 {lang==='en'?'Milestone':lang==='sv'?'Milstolpe':lang==='da'?'Milepæl':'Milepæl'}: {(state.milestoneReached||0).toLocaleString()} kr!
        </div>
      )}

      {/* First save WOW */}
      {firstSaveWow && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999, flexDirection:'column' }}>
          <div style={{ fontSize:80, animation:'bounce 0.5s ease' }}>🎉</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'#fff', marginTop:16, textAlign:'center' }}>
            {lang==='en'?'First saving!':lang==='sv'?'Första sparande!':lang==='da'?'Første opsparing!':'Første sparing!'}
          </div>
          <div style={{ fontSize:16, color:'rgba(255,255,255,0.6)', marginTop:8 }}>🔥 +XP</div>
        </div>
      )}


      {/* Header */}
      <div className="fade-up" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.25rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ fontSize:44, lineHeight:1 }}>
            {state.avatar || state.user?.avatar_emoji || state.user?.user_metadata?.avatar_emoji || '🌱'}
          </div>
          <div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:12, color:'var(--ink3)', fontWeight:600, letterSpacing:1, textTransform:'uppercase' }}>{lang==='en'?'Good day,':lang==='sv'?'God dag,':lang==='da'?'God dag,':'God dag,'}</div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:800, color:'var(--ink)', letterSpacing:-0.5 }}>
                {state.username || state.user?.username || state.user?.user_metadata?.full_name || state.user?.email?.split('@')[0] || ''}
              </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {state.streak > 0 && (
            <div style={{ background:'var(--r50)', border:'1px solid #FFCCCC', borderRadius:'var(--r-full)', padding:'5px 10px', fontSize:13, fontWeight:700, color:'var(--r600)' }}>🔥 {state.streak}</div>
          )}
          <div style={{ fontSize:12, color:'var(--ink3)' }}>
            {'Lvl ' + levelNum} · {rankName}
          </div>
        </div>
      </div>
      {/* Maskot + rank */}
      <div className="fade-up" style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem', display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ cursor:'default', flexShrink:0, position:'relative' }}>
          {savvyCelebrate ? (
            <div className="savvy-sprite-wrapper" style={{ width:56, height:56 }}>
              <div className="savvy-dancing" style={{
                width:307, height:1024, 
                backgroundImage:"url('/mascot/savvy-spritesheet.png')",
                backgroundRepeat:'no-repeat', backgroundSize:'1535px 1024px',
                transform:'scale(0.18)', transformOrigin:'top left',
              }}/>
            </div>
          ) : (
            <div className="savvy-sprite-wrapper" style={{ width:56, height:56 }}>
              <div className="savvy-dancing" style={{
                width:307, height:1024, 
                backgroundImage:"url('/mascot/savvy-spritesheet.png')",
                backgroundRepeat:'no-repeat', backgroundSize:'1535px 1024px',
                transform:'scale(0.18)', transformOrigin:'top left',
                animationDuration:'1.2s',
              }}/>
            </div>
          )}
        </div>
        <div style={{ flex:1, display:'flex', gap:6, alignItems:'center' }}>
          <img src="/mascot/savvy-happy.png" onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='block'); }} style={{ width:64, height:64, objectFit:'contain', flexShrink:0, flexShrink:0, marginLeft:-24 }}/>
          <div>
            <div style={{ fontSize:11, color:'var(--ink3)', marginBottom:2 }}>{rankName}</div>
            <div style={{ fontSize:13, color:'var(--ink)', fontWeight:600, lineHeight:1.4 }}>{getMascotMsg()}</div>
          </div>
        </div>
      </div>

      {/* Streak chain */}
      <div className="fade-up" style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--ink2)', marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
          <span className="streak-fire" style={{ fontSize:20 }}>🔥</span>
          <span style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:900, color: state.streak >= 7 ? '#FFD700' : state.streak >= 3 ? '#FF6B35' : 'var(--ink2)' }}>
            {state.streak}
          </span>
          <span>{lang==='en'?'day streak':lang==='sv'?'dagars streak':lang==='da'?'dages streak':'dagers streak'}</span>
          {state.streak >= 7 && <span style={{ fontSize:12, background:'#FFD700', color:'#000', borderRadius:999, padding:'2px 8px', fontWeight:700 }}>🏆 HOT</span>}
        </div>
        <div style={{ display:'flex', gap:4, justifyContent:'space-between' }}>
          {streakChain.map((day, i) => (
            <div key={i} style={{ flex:1, textAlign:'center' }}>
              <div style={{ width:'100%', aspectRatio:'1', borderRadius:'50%', background: day.hasSaved ? 'var(--g400)' : day.isToday ? 'var(--border)' : 'var(--surface)', border: day.isToday ? '2px solid var(--g400)' : '2px solid transparent', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, marginBottom:3 }}>
                {day.hasSaved ? '✓' : day.isToday ? '·' : ''}
              </div>
              <div style={{ fontSize:9, color:'var(--ink3)' }}>
                {lang==='en'?['Su','Mo','Tu','We','Th','Fr','Sa'][day.date.getDay()]:['Sø','Ma','Ti','On','To','Fr','Lø'][day.date.getDay()]}
              </div>
            </div>
          ))}

        </div>
        {!streakChain[6].hasSaved && (
          <div style={{ fontSize:11, color:'var(--r400)', marginTop:8, textAlign:'center', fontWeight:600 }}>
            ⚠️ {lang==='en'?'Save today to keep your streak!':lang==='sv'?'Spara idag för att hålla din streak!':lang==='da'?'Spar i dag for at holde din streak!':'Spar noe i dag for å ikke bryte streaken!'}
          </div>
        )}
      </div>

      {/* Daily challenge */}
      {state.dailyChallenge && (
        <div className="fade-up" style={{ background: state.dailyChallengeCompleted ? 'var(--g50)' : 'var(--card)', border:`1.5px solid ${state.dailyChallengeCompleted ? 'var(--g400)' : 'var(--border)'}`, borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:'var(--ink3)', marginBottom:2 }}>🎯 {lang==='en'?'Daily challenge':lang==='sv'?'Daglig utmaning':lang==='da'?'Daglig udfordring':'Daglig utfordring'}</div>
              <div style={{ fontSize:14, fontWeight:700, color:'var(--ink)' }}>{state.dailyChallenge[lang] || state.dailyChallenge.en}</div>
            </div>
            <div style={{ textAlign:'right', marginLeft:12 }}>
              {state.dailyChallengeCompleted
                ? <div style={{ fontSize:24 }}>✅</div>
                : <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:800, color:'var(--g400)' }}>+{state.dailyChallenge.xp} XP</div>
              }
            </div>
          </div>
        </div>
      )}


      {/* Daglig Budsjett Kort */}
      <div className="fade-up" style={{ background: budgetBg, border:`1.5px solid ${budgetColor}33`, borderRadius:'var(--r-lg)', padding:'1.25rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color: budgetColor, marginBottom:4 }}>{budgetLabel}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:36, fontWeight:800, color:'var(--ink)', lineHeight:1 }}>
              {fmt(remaining, lang)}
            </div>
            <div style={{ fontSize:12, color:'var(--ink3)', marginTop:4 }}>
              {state.payDay
                ? `${daysLeft} ${lang==='en'?'days to payday':lang==='sv'?'dagar till löning':lang==='da'?'dage til lønningsdag':'dager til lønningsdag'}`
                : lang==='en'?'left today':lang==='sv'?'kvar idag':lang==='da'?'tilbage i dag':'igjen i dag'}
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:11, color:'var(--ink3)' }}>{lang==='en'?'Spent':lang==='sv'?'Spenderat':lang==='da'?'Brugt':'Brukt'}</div>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--r400)' }}>{fmt(state.dailySpent||0, lang)}</div>
            <div style={{ fontSize:11, color:'var(--ink3)', marginTop:4 }}>{lang==='en'?'Budget':lang==='sv'?'Budget':lang==='da'?'Budget':'Budsjett'}/dag</div>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--ink2)' }}>{fmt(state.dailyBudget||0, lang)}</div>
          </div>
        </div>
        <div style={{ marginTop:14, background:'rgba(0,0,0,0.07)', borderRadius:99, height:10, overflow:'hidden' }}>
          <div style={{ width:`${Math.round(pct*100)}%`, height:'100%', background:`linear-gradient(90deg, var(--g400), var(--g600))`, borderRadius:99, transition:'width 0.5s ease', boxShadow:`0 0 8px ${budgetColor}66` }}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
          <span style={{ fontSize:10, color:'var(--ink3)' }}>{lang==='en'?'Spent':lang==='sv'?'Spenderat':lang==='da'?'Brugt':'Brukt'}: {fmt(state.dailySpent||0, lang)}</span>
          <span style={{ fontSize:10, color:'var(--ink3)' }}>{lang==='en'?'Budget':lang==='sv'?'Budget':lang==='da'?'Budget':'Budsjett'}: {fmt(state.dailyBudget||0, lang)}{lang==='en'?'/day':lang==='sv'?'/dag':lang==='da'?'/dag':'/dag'}</span>
        </div>
      </div>

      {/* Spare-knapp */}
      {(state.goals || []).some(g => g.target > 0) && (
      <div className="fade-up-1" style={{ marginBottom:'1rem' }}>
        {!showSave ? (
          <button onClick={() => setShowSave(true)} style={{ width:'100%', padding:14, background:'var(--g400)', color:'var(--ink)', border:'none', borderRadius:'var(--r-lg)', fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, cursor:'pointer' }}>{lang==='en'?'💰 Add savings':lang==='sv'?'💰 Lägg till sparande':lang==='da'?'💰 Tilføj opsparing':'💰 Legg til sparing'}</button>
        ) : (
          <div style={{ background:'var(--card)', border:'1.5px solid var(--g200)', borderRadius:'var(--r-lg)', padding:'1.25rem' }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:'1rem' }}>{lang==='en'?'💰 Add savings':lang==='sv'?'💰 Lägg till sparande':lang==='da'?'💰 Tilføj opsparing':'💰 Legg til sparing'}</div>
            {saveDone && (
              <div style={{ background:'var(--g50)', border:'1px solid var(--g200)', borderRadius:'var(--r-md)', padding:'0.75rem', marginBottom:10, textAlign:'center' }}>
                <div style={{ fontSize:20, marginBottom:3 }}>🎉</div>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--g600)' }}>{lang==='en'?'Saved!':lang==='sv'?'Sparat!':lang==='da'?'Sparet!':'Spart!'} +{saveAmount} kr</div>
                <div style={{ fontSize:12, color:'var(--ink3)', marginTop:4 }}>
                  {lang==='en'?'New daily budget':lang==='sv'?'Ny daglig budget':lang==='da'?'Nyt dagligt budget':'Nytt daglig budsjett'}: <strong>{Math.round(Math.max(0,(state.income - state.expenses)/ Math.max(1,daysLeft) - state.dailySpent/Math.max(1,daysLeft)))} lang==='en'?'$/day':lang==='sv'?'kr/dag':lang==='da'?'kr/dag':'kr/dag'</strong>
                </div>
              </div>
            )}
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:12, color:'var(--ink3)', display:'block', marginBottom:4 }}>{lang==='en'?'Amount to save':lang==='sv'?'Belopp att spara':lang==='da'?'Beløb at spare':'Beløp du sparer'}</label>
              <input type="number" value={saveAmount} onChange={e=>setSaveAmount(e.target.value)} placeholder={lang==='en'?'e.g. 200':lang==='sv'?'t.ex. 200':lang==='da'?'f.eks. 200':'f.eks. 200'} style={{ fontSize:16, fontWeight:600 }} onKeyDown={e=>e.key==='Enter'&&handleSave()}/>
              {saveAmt > 0 && overBudget && (
                <div style={{ fontSize:12, color:'var(--r400)', marginTop:4 }}>{lang==='en'?'⚠️ Max':lang==='sv'?'⚠️ Max':lang==='da'?'⚠️ Maks':'⚠️ Maks'} {fmt(remaining, lang)} {lang==='en'?'today':lang==='sv'?'idag':lang==='da'?'i dag':'i dag'}</div>
              )}
              {saveAmt > 0 && overGoal && !overBudget && (
                <div style={{ fontSize:12, color:'var(--r400)', marginTop:4 }}>{lang==='en'?`⚠️ Goal only needs ${fmt(goalRemaining, lang)} more`:lang==='sv'?`⚠️ Målet behöver bara ${fmt(goalRemaining, lang)} till`:lang==='da'?`⚠️ Målet har kun brug for ${fmt(goalRemaining, lang)} mere`:`⚠️ Målet trenger kun ${fmt(goalRemaining, lang)} mer`}</div>
              )}
              {lowWarning && (
                <div style={{ fontSize:12, color:'#92400E', marginTop:4, background:'#FFF7ED', padding:'6px 10px', borderRadius:'var(--r-sm)' }}>🟡 Er du sikker? Kun {fmt(afterSave, lang)} igjen etter sparing</div>
              )}
              {saveAmt > 0 && !overBudget && !overGoal && !lowWarning && (
                <div style={{ fontSize:12, color:'var(--g600)', marginTop:4 }}>✓ {fmt(afterSave, lang)} igjen · +{Math.round(saveAmt/10)} XP</div>
              )}
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:12, color:'var(--ink3)', display:'block', marginBottom:6 }}>{lang==='en'?'Which goal?':lang==='sv'?'Vilket mål?':lang==='da'?'Hvilket mål?':'Hvilket mål?'}</label>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {(state.goals || []).filter(g => g.target > 0).map(g => {
                  const gPct = Math.min(100, Math.round(g.saved/g.target*100));
                  const gRemaining = g.target - g.saved;
                  const selected = (saveGoalId || state.goals[0]?.id) === g.id;
                  const done = gPct >= 100;
                  return (
                    <button key={g.id} onClick={() => !done && setSaveGoalId(g.id)} style={{ padding:'10px 12px', border: selected ? `2px solid ${g.color}` : '1.5px solid var(--border)', borderRadius:'var(--r-md)', background: selected ? g.color+'11' : 'var(--surface)', display:'flex', alignItems:'center', gap:10, cursor: done ? 'default' : 'pointer', textAlign:'left', opacity: done ? 0.5 : 1 }}>
                      <span style={{ fontSize:18 }}>{g.emoji}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>{g.name}</div>
                        <div style={{ fontSize:11, color:'var(--ink3)' }}>{done ? lang==='en'?'Completed!':lang==='sv'?'Klart!':lang==='da'?'Færdig!':'Fullført!' : `${gPct}% · ${fmt(gRemaining, lang)} igjen`}</div>
                        {selected && saveAmt > 0 && !overBudget && !overGoal && (
                          <div style={{ marginTop:4 }}>
                            <div style={{ background:'rgba(0,0,0,0.08)', borderRadius:999, height:4, overflow:'hidden' }}>
                              <div style={{ width:Math.min(100, Math.round((g.saved+saveAmt)/g.target*100))+'%', height:'100%', background:g.color, borderRadius:999 }}/>
                            </div>
                            <div style={{ fontSize:10, color:g.color, marginTop:2, fontWeight:600 }}>Etter sparing: {Math.min(100, Math.round((g.saved+saveAmt)/g.target*100))}%</div>
                          </div>
                        )}
                      </div>
                      {selected && <span style={{ color:g.color, fontSize:14, fontWeight:700 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={handleSave} disabled={!saveAmt||saveAmt<=0||overBudget||overGoal} style={{ flex:1, padding:12, background:(!saveAmt||overBudget||overGoal)?'var(--surface)':'var(--g400)', color:(!saveAmt||overBudget||overGoal)?'var(--ink4)':'var(--ink)', border:'none', borderRadius:'var(--r-md)', fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, cursor:(!saveAmt||overBudget||overGoal)?'default':'pointer' }}>{lang==='en'?'Save now ✓':lang==='sv'?'Spara nu ✓':lang==='da'?'Spar nu ✓':'Spar nå ✓'}</button>
              <button onClick={() => { setShowSave(false); setSaveAmount(''); }} style={{ padding:'12px 16px', background:'var(--surface)', color:'var(--ink3)', border:'none', borderRadius:'var(--r-md)', fontSize:14, cursor:'pointer' }}>{lang==='en'?'Close':lang==='sv'?'Stäng':lang==='da'?'Luk':'Lukk'}</button>
            </div>
          </div>
        )}
        </div>
      )}

            {/* Ny bruker hint - kun hvis ingen mål og ingen tx */}
      {(state.goals || []).length === 0 && (state.transactions || []).length === 0  && (
        <div className="fade-up" style={{ background:'linear-gradient(135deg, rgba(34,201,122,0.1), rgba(0,212,255,0.05))', border:'1.5px solid rgba(34,201,122,0.25)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem', display:'flex', alignItems:'center', gap:10 }}>
          <img src="/mascot/savvy-excited.png" alt="Savvy" style={{ width:44, height:44, objectFit:'contain', flexShrink:0 }}/>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:800, color:'var(--g400)', marginBottom:3 }}>
              {lang==='en'?'Welcome! Set up your first goal 🎯':lang==='sv'?'Välkommen! Sätt upp ditt första mål 🎯':lang==='da'?'Velkommen! Opsæt dit første mål 🎯':'Velkommen! Sett opp ditt første mål 🎯'}
            </div>
            <div style={{ fontSize:11, color:'var(--ink3)', marginBottom:8 }}>
              {lang==='en'?'Add income in Profile, then start saving!':lang==='sv'?'Lägg till inkomst i Profil, börja sedan spara!':lang==='da'?'Tilføj indkomst i Profil, begynd derefter at spare!':'Legg til inntekt i Profil, så begynner sparingen!'}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {(state.income || 0) === 0 && (
                <button onClick={() => onNav && onNav('profil')} style={{ width:'100%', padding:'8px', borderRadius:'var(--r-md)', background:'var(--g400)', color:'var(--ink)', border:'none', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                  {lang==='en'?'Set up income →':lang==='sv'?'Ange inkomst →':lang==='da'?'Angiv indkomst →':'Legg til inntekt →'}
                </button>
              )}
              <button onClick={() => onNav && onNav('maal')} style={{ width:'100%', padding:'8px', borderRadius:'var(--r-md)', background:'var(--ink)', color:'var(--paper)', border:'none', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                🎯 {lang==='en'?'Add first savings goal':lang==='sv'?'Lägg till första sparmål':lang==='da'?'Tilføj første sparemål':'Legg til ditt første sparemål'}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Mål */}
      <div className="fade-up-2" style={{ marginBottom:'1rem' }}>
      {(state.goals || []).length > 0 ? (
        <div>
          <div>
                {(state.goals || []).slice(0, 3).map(goal => (
                  <div key={goal.id} onClick={() => onNav('maal')} style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'0.875rem 1rem', marginBottom:8, cursor:'pointer' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:20 }}>{goal.emoji}</span>
                        <div>
                          <div style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, color:'var(--ink)' }}>{goal.name}</div>
                          <div style={{ fontSize:11, color:'var(--ink3)' }}>{fmt(goal.saved, lang)} {lang==='en'?'of':lang==='sv'?'av':'av'} {fmt(goal.target, lang)}</div>
                        </div>
                      </div>
                      <div style={{ fontSize:13, fontWeight:700, color:goal.color }}>{goal.target === 0
                          ? (lang==='en'?'Set up →':lang==='sv'?'Ställ in →':lang==='da'?'Opsæt →':'Fullfør →')
                          : Math.round(goal.saved/goal.target*100)+'%'}</div>
                    </div>
                    <div style={{ background:'var(--surface)', borderRadius:999, height:8, overflow:'hidden' }}>
                      <div style={{ width:(goal.target > 0 ? Math.round(goal.saved/goal.target*100) : 0)+'%', height:'100%', background:goal.color, borderRadius:999 }}/>
                    </div>
                  </div>
                ))}
                {(state.goals || []).length > 3 && (
                  <button onClick={() => onNav('maal')} style={{ width:'100%', padding:'9px', background:'none', border:'1px solid var(--border)', borderRadius:'var(--r-md)', fontSize:13, color:'var(--ink3)', cursor:'pointer' }}>
                    {lang==='en'?'See all goals →':lang==='sv'?'Se alla mål →':lang==='da'?'Se alle mål →':'Se alle mål →'}
                  </button>
                )}
              </div>
        </div>
        ) : (
        <div onClick={() => onNav('maal')} style={{ background:'var(--surface)', border:'1.5px dashed var(--border2)', borderRadius:'var(--r-lg)', padding:'1rem', cursor:'pointer', textAlign:'center' }}>
          <div style={{ fontSize:24, marginBottom:6 }}>🎯</div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--ink2)' }}>{lang==='en'?'Add your first savings goal →':lang==='sv'?'Lägg till ditt första sparmål →':lang==='da'?'Tilføj dit første opsparingsmål →':'Legg til ditt første sparemål →'}</div>
        </div>
        )}
      </div>


      {/* Savings Graf */}
      <div className="fade-up-1" style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>
            {lang==='en'?'Savings overview':lang==='sv'?'Sparöversikt':lang==='da'?'Spareoversigt':'Spareoversikt'}
          </div>
          <div style={{ display:'flex', gap:4 }}>
            {[['week', lang==='en'?'Week':lang==='sv'?'Vecka':lang==='da'?'Uge':'Uke'],
              ['month', lang==='en'?'Month':lang==='sv'?'Månad':lang==='da'?'Måned':'Måned'],
              ['year', lang==='en'?'Year':lang==='sv'?'År':lang==='da'?'År':'År']
            ].map(([p,label]) => (
              <button key={p} onClick={e=>{ e.stopPropagation(); if(!isPro && p!=='week') return; setGraphPeriod(p); setGraphOffset(0); }}
                style={{ padding:'4px 10px', borderRadius:'var(--r-full)', fontSize:11, fontWeight:700, border:'none', cursor: isPro||p==='week'?'pointer':'not-allowed',
                  background: graphPeriod===p ? 'var(--g400)' : 'var(--surface)',
                  color: graphPeriod===p ? 'var(--ink)' : (!isPro&&p!=='week') ? 'var(--ink4)' : 'var(--ink3)',
                  opacity: !isPro&&p!=='week' ? 0.5 : 1 }}>
                {!isPro&&p!=='week' ? `${label} 🔒` : label}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation forrige/neste */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <button onClick={() => setGraphOffset(o => o - 1)}
            style={{ padding:'4px 12px', borderRadius:'var(--r-md)', border:'1px solid var(--border)', background:'var(--surface)', cursor:'pointer', fontSize:13, color:'var(--ink2)' }}>
            ‹ {lang==='en'?'Prev':lang==='sv'?'Förra':lang==='da'?'Forrige':'Forrige'}
          </button>
          <div style={{ fontSize:11, color:'var(--ink3)', fontWeight:600 }}>
            {graphOffset === 0
              ? (lang==='en'?'This week':lang==='sv'?'Denna vecka':lang==='da'?'Denne uge':'Denne uken')
              : graphOffset === -1
              ? (lang==='en'?'Last week':lang==='sv'?'Förra veckan':lang==='da'?'Sidste uge':'Forrige uke')
              : `${Math.abs(graphOffset)} ${lang==='en'?'weeks ago':lang==='sv'?'veckor sedan':lang==='da'?'uger siden':'uker siden'}`}
          </div>
          <button onClick={() => setGraphOffset(o => Math.min(0, o + 1))} disabled={graphOffset >= 0}
            style={{ padding:'4px 12px', borderRadius:'var(--r-md)', border:'1px solid var(--border)', background: graphOffset >= 0 ? 'var(--surface)' : 'var(--surface)', cursor: graphOffset >= 0 ? 'default' : 'pointer', fontSize:13, color: graphOffset >= 0 ? 'var(--ink4)' : 'var(--ink2)', opacity: graphOffset >= 0 ? 0.4 : 1 }}>
            {lang==='en'?'Next':lang==='sv'?'Nästa':lang==='da'?'Næste':'Neste'} ›
          </button>
        </div>
        <div style={{ display:'flex', alignItems:'flex-end', gap:4, height:80 }}>
          {_graphLabels.map((d,i) => {
            const saved = _graphDaySaved(d);
            const spent = _graphDaySpent(d);
            const hSaved = Math.max(4, Math.round((saved/_graphMaxSaved)*70));
            const hSpent = Math.max(4, Math.round((spent/_graphMaxSaved)*70));
            return (
              <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center' }}>
                <div style={{ width:'100%', display:'flex', gap:2, alignItems:'flex-end', justifyContent:'center' }}>
                  {saved>0 && <div style={{ flex:1, height:hSaved, background:'var(--g400)', borderRadius:'3px 3px 0 0' }}/>}
                  {spent>0 && <div style={{ flex:1, height:hSpent, background:'var(--r400)', borderRadius:'3px 3px 0 0' }}/>}
                  {saved===0&&spent===0 && <div style={{ flex:1, height:4, background:'var(--border)', borderRadius:3 }}/>}
                </div>
                <div style={{ fontSize:9, color:'var(--ink3)', marginTop:3 }}>{d.getDate()}/{d.getMonth()+1}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display:'flex', gap:12, marginTop:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--ink3)' }}>
            <div style={{ width:8, height:8, background:'var(--g400)', borderRadius:2 }}/>{lang==='en'?'Saved':lang==='sv'?'Sparat':lang==='da'?'Sparet':'Spart'}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--ink3)' }}>
            <div style={{ width:8, height:8, background:'var(--r400)', borderRadius:2 }}/>{lang==='en'?'Spent':lang==='sv'?'Spenderat':lang==='da'?'Brugt':'Brukt'}
          </div>
        </div>
      </div>

      {/* FOMO */}
      {state.friendsSavingToday > 0 && (
        <div className="fade-up" style={{ background:'var(--surface)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'0.875rem 1rem', marginBottom:'1rem', fontSize:13, color:'var(--ink2)' }}>
          👥 {state.friendsSavingToday} {lang==='en' ? `friend${state.friendsSavingToday>1?'s':''} saving right now — don't fall behind!` : lang==='sv' ? `vän${state.friendsSavingToday>1?'ner':''} sparar just nu — bli inte efter!` : lang==='da' ? `ven${state.friendsSavingToday>1?'ner':''} sparer lige nu — bliv ikke bagud!` : `venn${state.friendsSavingToday>1?'er':''} sparer akkurat nå — ikke bli hengende etter!`}
        </div>
      )}

      {/* Loss aversion - streak warning */}
      {state.streak >= 3 && !streakChain[6].hasSaved && (
        <div className="fade-up" style={{ background:'var(--r50)', border:'1.5px solid #FFCCCC', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--r600)', marginBottom:4 }}>
            ⚠️ {lang==='en'?`Don't lose your ${state.streak}-day streak!`:lang==='sv'?`Förlora inte din ${state.streak}-dagars streak!`:lang==='da'?`Mist ikke din ${state.streak}-dages streak!`:`Ikke mist ${state.streak}-dagers streaken din!`}
          </div>
          <div style={{ fontSize:12, color:'var(--r400)' }}>
            {lang==='en'?'Save something today to keep it going 🔥':lang==='sv'?'Spara något idag för att hålla den igång 🔥':lang==='da'?'Spar noget i dag for at holde den kørende 🔥':'Spar noe i dag for å holde den gående 🔥'}
          </div>
        </div>
      )}

      {/* Commitment mechanic */}
      {!commitmentDone && state.streak === 0 && (
        <div className="fade-up" style={{ background:'var(--card)', border:'1.5px solid var(--g200)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem', textAlign:'center' }}>
          <div style={{ fontSize:16, fontWeight:800, color:'var(--ink)', marginBottom:4 }}>
            {lang==='en'?'🤝 Can you save for 7 days?':lang==='sv'?'🤝 Kan du spara i 7 dagar?':lang==='da'?'🤝 Kan du spare i 7 dage?':'🤝 Klarer du å spare i 7 dager?'}
          </div>
          <div style={{ fontSize:12, color:'var(--ink3)', marginBottom:12 }}>
            {lang==='en'?'Most people fail. Are you different?':lang==='sv'?'De flesta misslyckas. Är du annorlunda?':lang==='da'?'De fleste fejler. Er du anderledes?':'De fleste feiler. Er du annerledes?'}
          </div>
          <button onClick={makeCommitment} style={{ padding:'10px 24px', background:'var(--g400)', border:'none', borderRadius:'var(--r-full)', fontSize:13, fontWeight:700, cursor:'pointer', color:'var(--ink)' }}>
            {lang==='en'?'Yes, I accept the challenge! (+50 XP)':lang==='sv'?'Ja, jag accepterar utmaningen! (+50 XP)':lang==='da'?'Ja, jeg accepterer udfordringen! (+50 XP)':'Ja, jeg tar utfordringen! (+50 XP)'}
          </button>
        </div>
      )}

      {/* Share card modal */}
      {showShareCard && <ShareCard onClose={() => setShowShareCard(false)}/>}

      {/* Share streak button */}
      {state.streak >= 3 && (
        <div className="fade-up" style={{ marginBottom:'1rem' }}>
          <button onClick={() => setShowShareCard(true)} style={{ width:'100%', padding:'10px', background:'var(--surface)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', fontSize:13, fontWeight:600, cursor:'pointer', color:'var(--ink2)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
            📤 {lang==='en'?`Share your ${state.streak}-day streak!`:lang==='sv'?`Dela din ${state.streak}-dagars streak!`:lang==='da'?`Del din ${state.streak}-dages streak!`:`Del ${state.streak}-dagers streaken din!`}
          </button>
        </div>
      )}

      

      {/* Familie + Venner */}
      <FamilyGroup lang={lang} state={state} dispatch={dispatch}/>

      {/* Push-varsler */}
      {!pushEnabled && (
        <div className="fade-up-1" style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)', marginBottom:2 }}>{lang==='en'?'🔔 Daily reminders':lang==='sv'?'🔔 Dagliga påminnelser':lang==='da'?'🔔 Daglige påmindelser':'🔔 Daglige påminnelser'}</div>
<div style={{ fontSize:11, color:'var(--ink3)' }}>{t(lang,'reminderDesc')}</div>
          </div>
          <button onClick={enablePush} style={{ padding:'8px 14px', background:'var(--g400)', border:'none', borderRadius:'var(--r-full)', fontSize:12, fontWeight:700, cursor:'pointer', color:'var(--ink)', whiteSpace:'nowrap' }}>
            {lang==='en'?'Turn on':lang==='sv'?'Slå på':lang==='da'?'Slå til':'Slå på'}
          </button>
        </div>
        )}

      {/* Del sparemål */}
      {(state.goals || []).filter(g => g.saved > 0).length > 0 && (
        <div className="fade-up-1" style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--ink2)', marginBottom:8 }}>{t(lang,'shareGoalTitle')}</div>
          {(state.goals || []).filter(g => g.saved > 0).slice(0, 2).map(goal => (
            <div key={goal.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontSize:13, color:'var(--ink)' }}>{goal.icon} {goal.name} — {goal.target === 0
                          ? (lang==='en'?'Set up →':lang==='sv'?'Ställ in →':lang==='da'?'Opsæt →':'Fullfør →')
                          : Math.round(goal.saved/goal.target*100)+'%'}</div>
              <button onClick={() => shareGoal(goal)} style={{ padding:'5px 12px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r-full)', fontSize:11, fontWeight:600, cursor:'pointer', color:'var(--ink2)' }}>
                Del 📤
              </button>
            </div>
          ))}
        </div>
        )}

      



      {/* Prediktiv */}
      {PLAN_LIMITS[state.plan]?.hasPrediction && daysLeft !== null && (
      <div className="fade-up-1" style={{ background: daysLeft<7?'var(--r50)':'var(--g50)', border:`1.5px solid ${daysLeft<7?'var(--r100)':'var(--g200)'}`, borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem', display:'flex', gap:10 }}>
        <div style={{ fontSize:20 }}>🔮</div>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:daysLeft<7?'var(--r600)':'var(--g800)', marginBottom:2 }}>{lang==='en'?'Predictive economy':lang==='sv'?'Prediktiv ekonomi':lang==='da'?'Prediktiv økonomi':'Prediktiv økonomi'}</div>
          <div style={{ fontSize:13, color:daysLeft<7?'var(--r400)':'var(--g600)' }}>{daysLeft<7?lang==='en'?`⚠️ At this pace you'll run out in ${daysLeft} days!`:lang==='sv'?`⚠️ I detta tempo tar pengarna slut om ${daysLeft} dagar!`:lang==='da'?`⚠️ I dette tempo løber du tør om ${daysLeft} dage!`:`⚠️ I dette tempoet går du tom om ${daysLeft} dager!`:lang==='en'?`✅ You have enough for about ${daysLeft} more days`:lang==='sv'?`✅ Du har nog i ungefär ${daysLeft} dagar till`:lang==='da'?`✅ Du har nok i ca. ${daysLeft} dage til`:`✅ Du har nok i ca. ${daysLeft} dager til`}</div>
        </div>
        </div>
      )}

      {/* XP + Score */}
      <div className="fade-up-2" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:'1rem' }}>
      <div style={{ background:'var(--ink)', borderRadius:'var(--r-lg)', padding:'1rem', display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--g400)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:14, fontWeight:800, color:'var(--ink)', flexShrink:0 }}>{levelNum}</div>
        <div>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)' }}>Level</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:800, color:'#fff' }}>{xpInLevel} / {XP_PER_LEVEL} XP</div>
          <div style={{ background:'rgba(255,255,255,0.1)', borderRadius:999, height:4, marginTop:3, overflow:'hidden' }}>
            <div style={{ width:xpPct+'%', height:'100%', background:'var(--g400)', borderRadius:999 }}/>
          </div>
        </div>
        </div>
      <div style={{ background:'var(--g50)', border:'1.5px solid var(--g200)', borderRadius:'var(--r-lg)', padding:'1rem', cursor:'pointer' }} onClick={() => onNav('sosial')}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
          <div style={{ fontSize:11, color:'var(--g800)', fontWeight:600 }}>{lang==='en'?'Savings score':lang==='sv'?'Sparpoäng':lang==='da'?'Sparepoint':'Sparescore'}</div>
          <span style={{ fontSize:18 }}>🏆</span>
        </div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'var(--g400)' }}>{myScore}</div>
        <div style={{ fontSize:10, color:'var(--g600)' }}>{lang==='en'?'See leaderboard →':lang==='sv'?'Se topplista →':lang==='da'?'Se leaderboard →':'Se ledertavle →'}</div>
        </div>
      </div>



      {/* Tips */}
      <div className="fade-up-3" style={{ background:'#FFF7ED', border:'1.5px solid #FED7AA', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
      <div style={{ display:'flex', gap:10 }}>
        <div style={{ fontSize:20 }}>💡</div>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:'#C2410C', marginBottom:3 }}>Tips</div>
          <div style={{ fontSize:13, color:'#7C2D12', lineHeight:1.5 }}>{state.weeklyInsight.savingTip}</div>
        </div>
        </div>
      </div>

      {/* Sparefeed */}
      <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:'0.75rem' }}>📱 Sparefeed</div>
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:'1rem' }}>
        {getPersonalizedTips(lang, state).map((tip, i) => (
        <div key={i} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', display:'flex', gap:12 }}>
          <div style={{ fontSize:24, flexShrink:0 }}>{tip.emoji}</div>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>{tip.title}</div>
              <div style={{ fontSize:10, background:'var(--g50)', color:'var(--g800)', padding:'2px 6px', borderRadius:999, fontWeight:600 }}>{tip.tag}</div>
            </div>
            <div style={{ fontSize:13, color:'var(--ink2)', lineHeight:1.5 }}>{tip.body}</div>
          </div>
        </div>
        ))}
      </div>

      {/* Siste transaksjoner */}
      {state.transactions.slice(0,3).length > 0 && (
      <div style={{ marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
          <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--ink)' }}>{lang==='en'?'Recent transactions':lang==='sv'?'Senaste transaktioner':lang==='da'?'Seneste transaktioner':'Siste transaksjoner'}</div>
          <button onClick={() => onNav('budsjett')} style={{ fontSize:12, color:'var(--g600)', background:'none', border:'none', fontWeight:600, cursor:'pointer' }}>{lang==='en'?'See all →':lang==='sv'?'Se alla →':lang==='da'?'Se alle →':'Se alle →'}</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {(state.transactions || []).slice(0,3).map(tx => {
            const isSaving = tx.cat === 'Sparing';
            const meta = CAT_META[tx.cat] || { icon:'📦', bg:'#F8FAFC', color:'#475569' };
            const relatedGoal = isSaving ? (state.goals || []).find(g => tx.desc.includes(g.name)) : null;
            const goalDone = relatedGoal && relatedGoal.saved >= relatedGoal.target;
            return (
              <div key={tx.id} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.75rem', display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:38, height:38, borderRadius:'var(--r-sm)', background: isSaving ? '#F5F3FF' : meta.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                  {isSaving ? '🪙' : meta.icon}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:500, color:'var(--ink)' }}>{tx.desc === 'fixed_expenses_key' ? (lang==='en'?'Fixed expenses':lang==='sv'?'Fasta utgifter':lang==='da'?'Faste udgifter':'Faste utgifter') : tx.desc}</div>
                  <div style={{ fontSize:11, color:'var(--ink3)' }}>{tx.cat} · {tx.time}</div>
                </div>
                <div style={{ fontSize:14, fontWeight:700, color: isSaving ? '#7C3AED' : tx.amount < 0 ? 'var(--r400)' : 'var(--g400)' }}>
                  {isSaving ? `+${fmt(tx.amount, lang)}` : tx.amount > 0 ? `+${fmt(tx.amount, lang)}` : `-${fmt(tx.amount, lang)}`}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      <div style={{ background:'var(--surface)', borderRadius:'var(--r-md)', padding:'0.875rem', display:'flex', gap:10, alignItems:'flex-start' }}>
        <span style={{ fontSize:16 }}>🧠</span>
      <div style={{ fontSize:12, color:'var(--ink2)', lineHeight:1.6 }}>{TIPS[tipIdx]}</div>
      </div>

      <div style={{ height:16 }}/>



      {/* Referral banner */}
      {(state.referralCount || 0) < 20 && (
        <div style={{ background:'linear-gradient(135deg,rgba(34,201,122,0.12),rgba(0,212,255,0.08))', border:'1.5px solid rgba(34,201,122,0.25)', borderRadius:'var(--r-lg)', padding:'14px 16px', marginBottom:'1rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:'var(--g400)' }}>🎁 {lang==='en'?'Invite friends, earn Advanced!':lang==='sv'?'Bjud in vänner, tjäna Advanced!':lang==='da'?'Inviter venner, tjen Advanced!':'Inviter venner, tjen Advanced!'}</div>
            <div style={{ fontSize:11, color:'var(--ink3)', marginTop:3 }}>
              {lang==='en'?`${state.referralCount||0}/10 friends — 10 days free!`:lang==='sv'?`${state.referralCount||0}/10 vänner — 10 dagar gratis!`:lang==='da'?`${state.referralCount||0}/10 venner — 10 dage gratis!`:`${state.referralCount||0}/10 venner — 10 dager gratis!`}
            </div>
            <div style={{ marginTop:6, background:'rgba(0,0,0,0.08)', borderRadius:99, height:4, width:120 }}>
              <div style={{ width:`${Math.min(100,((state.referralCount||0)/10)*100)}%`, height:'100%', background:'var(--g400)', borderRadius:99 }}/>
            </div>
          </div>
          <div style={{ fontSize:24 }}>🚀</div>
        </div>
      )}

      {/* Maskot - Savvy */}
      {showMascot && (
        <div onClick={() => setShowMascot(false)} style={{ position:'fixed', bottom:70, left:'50%', transform:'translateX(-50%)', zIndex:9999, display:'flex', flexDirection:'column', alignItems:'center', cursor:'pointer' }}>
          <img
            src={`/mascot/${
              mascotMood === 'excited' ? 'savvy-excited' :
              mascotMood === 'worried' ? 'savvy-worried' :
              mascotMood === 'sleepy' ? 'savvy-sleepy' :
              mascotMood === 'celebrate' ? 'savvy-celebrate' :
              mascotMood === 'angry' ? 'savvy-angry' :
              mascotMood === 'star' ? 'savvy-star' :
              mascotMood === 'flex' ? 'savvy-flex' :
              mascotMood === 'singing' ? 'savvy-singing' :
              mascotMood === 'shocked' ? 'savvy-shocked' :
              mascotMood === 'party' ? 'savvy-party' :
              'savvy-happy'
            }.png`}
            alt="Savvy"
            style={{
              width: 140,
              height: 140,
              objectFit: 'contain',
              animation: mascotMood === 'excited' ? 'savvyBounce 0.5s ease infinite' :
                         mascotMood === 'worried' ? 'savvyShake 0.4s ease infinite' :
                         mascotMood === 'celebrate' ? 'savvySpin 0.6s ease infinite' :
                         mascotMood === 'sleepy' ? 'savvyPulse 2s ease infinite' :
                         mascotMood === 'angry' ? 'savvyShake 0.3s ease infinite' :
                         mascotMood === 'star' ? 'savvyBounce 0.6s ease infinite' :
                         mascotMood === 'flex' ? 'savvySpin 1s ease infinite' :
                         mascotMood === 'singing' ? 'savvyFloat 1s ease-in-out infinite' :
                         mascotMood === 'shocked' ? 'savvyShake 0.2s ease infinite' :
                         mascotMood === 'party' ? 'savvyBounce 0.4s ease infinite' :
                         'savvyFloat 3s ease-in-out infinite',
              filter: 'drop-shadow(0 8px 24px rgba(34,201,122,0.5))',
            }}
          />
          <div style={{ background:'linear-gradient(135deg,#16A34A,#15803D)', color:'#fff', borderRadius:20, padding:'12px 20px', fontSize:13, fontWeight:700, maxWidth:280, textAlign:'center', marginTop:-16, boxShadow:'0 8px 30px rgba(22,163,74,0.5)', border:'2px solid rgba(255,255,255,0.25)', lineHeight:1.5, position:'relative' }}>
            <div style={{ position:'absolute', top:-11, left:'50%', transform:'translateX(-50%)', background:'#15803D', borderRadius:99, padding:'2px 12px', fontSize:11, fontWeight:700, border:'2px solid rgba(255,255,255,0.3)', whiteSpace:'nowrap' }}>Savvy 🌱</div>
            <div style={{ marginTop:4 }}>{mascotMsg}</div>
            <div style={{ fontSize:11, opacity:0.6, marginTop:6 }}>{lang==='en'?'tap to close':lang==='sv'?'tryck för att stänga':lang==='da'?'tryk for at lukke':'trykk for å lukke'}</div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes savvyFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes savvyBounce { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-20px) scale(1.05)} }
        @keyframes savvyShake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-8px)} 75%{transform:translateX(8px)} }
        @keyframes savvySpin { 0%{transform:rotate(0deg) scale(1)} 50%{transform:rotate(10deg) scale(1.1)} 100%{transform:rotate(0deg) scale(1)} }
        @keyframes savvyPulse { 0%,100%{transform:scale(1) rotate(-3deg)} 50%{transform:scale(0.95) rotate(3deg)} }
      `}</style>



    </div>
  );
}
